import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Settings2, AlertTriangle, Users2 } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Skeleton,
  Badge,
  Separator,
} from '@ouropix/ui';
import {
  useMyAlertPreferences,
  useAdminAlertPreferences,
} from '@/hooks/useAlerts';
import { PreferencesForm } from '@/components/alerts/PreferencesForm';
import { useAuth } from '@/hooks/useAuth';
import { api } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import type { AdminPreferencesRow } from '@/components/alerts/types';

export const Route = createFileRoute('/_admin/alertas-seguranca/preferencias')({
  component: AlertPreferenciasPage,
});

// ── Dialog para editar prefs de outro admin ──────────────

function AdminPrefsDialog({
  adminId,
  adminName,
  open,
  onOpenChange,
}: {
  adminId: string;
  adminName: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { data, isLoading } = useAdminAlertPreferences(adminId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Preferencias de {adminName}</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="space-y-3 py-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : data ? (
          <PreferencesForm
            preferences={data}
            adminId={adminId}
            onSaved={() => onOpenChange(false)}
          />
        ) : (
          <p className="text-sm text-muted-foreground py-4">
            Nao foi possivel carregar as preferencias.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Tabela de admins (super_admin) ────────────────────────

function AdminsPreferencesTable() {
  const [editingAdmin, setEditingAdmin] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const { data: adminsPrefs, isLoading } = useQuery<AdminPreferencesRow[]>({
    queryKey: ['alerts', 'preferences', 'all-admins'],
    queryFn: async () => {
      const res = await api.get('/admin/alerts/preferences');
      return res.data?.data ?? res.data ?? [];
    },
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (!adminsPrefs?.length) {
    return (
      <p className="text-sm text-muted-foreground text-center py-6">
        Nenhum admin encontrado.
      </p>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table
          className="w-full text-sm"
          role="table"
          aria-label="Preferencias de alertas dos admins"
        >
          <thead>
            <tr className="border-b">
              <th className="text-left px-4 py-2 text-xs font-semibold text-muted-foreground">
                Admin
              </th>
              <th className="text-left px-4 py-2 text-xs font-semibold text-muted-foreground">
                Email
              </th>
              <th className="text-left px-4 py-2 text-xs font-semibold text-muted-foreground">
                Email hab.
              </th>
              <th className="text-left px-4 py-2 text-xs font-semibold text-muted-foreground">
                SMS hab.
              </th>
              <th className="text-left px-4 py-2 text-xs font-semibold text-muted-foreground hidden sm:table-cell">
                Overrides
              </th>
              <th className="text-left px-4 py-2 text-xs font-semibold text-muted-foreground hidden md:table-cell">
                Atualizado
              </th>
              <th className="text-left px-4 py-2 text-xs font-semibold text-muted-foreground">
                Acoes
              </th>
            </tr>
          </thead>
          <tbody>
            {adminsPrefs.map((row) => (
              <tr
                key={row.admin_id}
                className="border-b last:border-0 hover:bg-muted/20"
                role="row"
              >
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{row.admin_name}</span>
                    {row.has_critical_disabled && (
                      <span title="Este admin tem canais desabilitados para eventos CRITICAL">
                        <AlertTriangle
                          className="h-3.5 w-3.5 text-yellow-500"
                          aria-label="Aviso: canais CRITICAL desabilitados"
                        />
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">
                  {row.admin_email}
                </td>
                <td className="px-4 py-2.5">
                  <Badge
                    variant="outline"
                    className={
                      row.email_enabled
                        ? 'text-green-400 border-green-500/30'
                        : 'text-red-400 border-red-500/30'
                    }
                  >
                    {row.email_enabled ? 'Sim' : 'Nao'}
                  </Badge>
                </td>
                <td className="px-4 py-2.5">
                  <Badge
                    variant="outline"
                    className={
                      row.sms_enabled
                        ? 'text-green-400 border-green-500/30'
                        : 'text-gray-400 border-gray-500/30'
                    }
                  >
                    {row.sms_enabled ? 'Sim' : 'Nao'}
                  </Badge>
                </td>
                <td className="px-4 py-2.5 hidden sm:table-cell">
                  <Badge variant="outline" className="text-xs">
                    {row.override_count}
                  </Badge>
                </td>
                <td className="px-4 py-2.5 hidden md:table-cell text-xs text-muted-foreground font-mono">
                  {row.last_updated
                    ? new Date(row.last_updated).toLocaleDateString('pt-BR')
                    : '—'}
                </td>
                <td className="px-4 py-2.5">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() =>
                      setEditingAdmin({ id: row.admin_id, name: row.admin_name })
                    }
                    aria-label={`Editar preferencias de ${row.admin_name}`}
                  >
                    Editar
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingAdmin && (
        <AdminPrefsDialog
          adminId={editingAdmin.id}
          adminName={editingAdmin.name}
          open={!!editingAdmin}
          onOpenChange={(v) => {
            if (!v) setEditingAdmin(null);
          }}
        />
      )}
    </>
  );
}

// ── Pagina principal ──────────────────────────────────────

function AlertPreferenciasPage() {
  const { session } = useAuth();
  const isSuperAdmin = session?.role === 'superadmin';

  const { data: myPrefs, isLoading: loadingMyPrefs } = useMyAlertPreferences();

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Settings2 className="h-7 w-7" aria-hidden="true" />
          Preferencias de Alertas
        </h1>
        <p className="text-muted-foreground text-sm">
          Configure como deseja receber notificacoes de seguranca.
        </p>
      </div>

      {/* Minhas preferencias */}
      <section aria-labelledby="my-prefs-heading">
        <h2
          id="my-prefs-heading"
          className="text-lg font-semibold mb-3 flex items-center gap-2"
        >
          Minhas preferencias
        </h2>

        {loadingMyPrefs ? (
          <div className="space-y-3">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : myPrefs ? (
          <PreferencesForm preferences={myPrefs} />
        ) : (
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-sm text-muted-foreground">
                Nao foi possivel carregar suas preferencias.
              </p>
            </CardContent>
          </Card>
        )}
      </section>

      {/* Preferencias de outros admins (super_admin) */}
      {isSuperAdmin && (
        <>
          <Separator />
          <section aria-labelledby="team-prefs-heading">
            <h2
              id="team-prefs-heading"
              className="text-lg font-semibold mb-3 flex items-center gap-2"
            >
              <Users2 className="h-5 w-5" aria-hidden="true" />
              Preferencias da equipe
              <Badge variant="outline" className="text-xs">
                Super Admin
              </Badge>
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              Visualize e edite as preferencias de notificacao de todos os admins.
            </p>

            <Card>
              <CardContent className="p-0">
                <AdminsPreferencesTable />
              </CardContent>
            </Card>
          </section>
        </>
      )}
    </div>
  );
}
